import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { safeParseJson } from '../utils/auth';

const LOCAL_ORDERS_KEY = 'best3_local_orders_v1';

const FULFILLMENT_STEPS = [
  { key: 'unfulfilled', label: 'Order Placed', icon: '📝' },
  { key: 'processing', label: 'Processing', icon: '⚙️' },
  { key: 'shipped', label: 'Shipped', icon: '🚚' },
  { key: 'delivered', label: 'Delivered', icon: '✅' }
];

const OrderTrackingPage = () => {
  const [searchParams] = useSearchParams();
  const initialOrderNum = searchParams.get('order') || '';
  const initialEmail = searchParams.get('email') || '';

  const [orderNumber, setOrderNumber] = useState(initialOrderNum);
  const [email, setEmail] = useState(initialEmail);
  const [lookedUpOrder, setLookedUpOrder] = useState(null);
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const findLocalOrder = (ordNum, mail) => {
    try {
      const localList = JSON.parse(localStorage.getItem(LOCAL_ORDERS_KEY) || '[]');
      if (!Array.isArray(localList)) return null;
      return (
        localList.find(
          (o) =>
            o.order_number?.toLowerCase() === ordNum.toLowerCase() &&
            (!mail || o.customer_email?.toLowerCase() === mail.toLowerCase())
        ) || null
      );
    } catch (_e) {
      return null;
    }
  };

  const handleLookup = useCallback(
    async (ordOverride, emailOverride) => {
      const targetOrder = (ordOverride !== undefined ? ordOverride : orderNumber).trim();
      const targetEmail = (emailOverride !== undefined ? emailOverride : email).trim();

      if (!targetOrder) {
        setErrorMsg('Please enter your Order Number (e.g. B3T-...).');
        return;
      }

      setLoading(true);
      setErrorMsg('');

      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      try {
        const res = await fetch(
          `/best3intennis-api/api/get_order_status.php?order_number=${encodeURIComponent(
            targetOrder
          )}&email=${encodeURIComponent(targetEmail)}`,
          { headers }
        );
        const text = await res.text();
        const data = safeParseJson(text);

        if (res.ok && data && data.success && data.order) {
          setLookedUpOrder(data.order);
          setLoading(false);
          return;
        }
        if (data && data.message && res.status >= 400 && res.status < 500 && res.status !== 404) {
          const localMatch = findLocalOrder(targetOrder, targetEmail);
          if (localMatch) {
            setLookedUpOrder(localMatch);
            setLoading(false);
            return;
          }
          setErrorMsg(data.message);
          setLookedUpOrder(null);
          setLoading(false);
          return;
        }
      } catch (_err) {
        // Fallback to local order snapshot
      }

      const localMatch = findLocalOrder(targetOrder, targetEmail);
      if (localMatch) {
        setLookedUpOrder(localMatch);
      } else {
        setErrorMsg('No matching order found. Please check your Order Number and Email.');
        setLookedUpOrder(null);
      }
      setLoading(false);
    },
    [orderNumber, email]
  );

  // Load logged-in user's orders or URL query order on mount
  useEffect(() => {
    if (initialOrderNum) {
      handleLookup(initialOrderNum, initialEmail);
    }

    const token = localStorage.getItem('token');
    if (token) {
      fetch('/best3intennis-api/api/get_order_status.php', {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then((r) => r.text())
        .then((t) => {
          const d = safeParseJson(t);
          if (d && d.success && Array.isArray(d.orders)) {
            setMyOrders(d.orders);
          }
        })
        .catch(() => {});
    }
  }, [initialOrderNum, initialEmail, handleLookup]);

  const getStepIndex = (status) => {
    if (status === 'delivered') return 3;
    if (status === 'shipped') return 2;
    if (status === 'processing') return 1;
    return 0;
  };

  return (
    <div className="min-h-screen py-12 px-4 bg-gradient-to-b from-green-50/70 to-white" data-testid="order-tracking-page">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="text-center">
          <NavLink to="/extras-shop" className="text-xs font-bold text-green-700 hover:underline">
            ← Back to Pro Shop
          </NavLink>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-green-900 mt-2">
            Track Your Order
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Enter your Best3inTennis Order Number and Email to check real-time fulfillment and shipping status.
          </p>
        </div>

        {/* Lookup Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleLookup();
          }}
          className="bg-white rounded-2xl shadow-md border border-gray-200 p-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Order Number *</label>
              <input
                type="text"
                required
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. B3T-20261017-AB12C"
                data-testid="track-order-number-input"
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Billing / Receipt Email *</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="player@example.com"
                data-testid="track-order-email-input"
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
          </div>

          {errorMsg && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-xs font-semibold border border-red-200">
              {errorMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="track-order-submit-btn"
            className="w-full bg-green-700 hover:bg-green-800 text-white font-bold py-3 px-6 rounded-xl shadow-sm transition cursor-pointer disabled:opacity-60"
          >
            {loading ? 'Looking up Order...' : '🔍 Track Order Status'}
          </button>
        </form>

        {/* Order Status Result Card */}
        {lookedUpOrder && (
          <div
            className="bg-white rounded-2xl shadow-lg border border-green-200 overflow-hidden"
            data-testid="tracked-order-card"
          >
            <div className="bg-gradient-to-r from-green-800 to-green-700 text-white px-6 py-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="text-xs uppercase text-green-200 font-bold">Order Number</span>
                <h2 className="text-xl font-extrabold" data-testid="tracked-order-number">
                  {lookedUpOrder.order_number}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-extrabold uppercase bg-yellow-400 text-green-950">
                  Payment: {lookedUpOrder.payment_status}
                </span>
                <span
                  className="px-3 py-1 rounded-full text-xs font-extrabold uppercase bg-white/20 text-white"
                  data-testid="tracked-fulfillment-status"
                >
                  Status: {lookedUpOrder.fulfillment_status}
                </span>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Progress Stepper */}
              {lookedUpOrder.fulfillment_status !== 'cancelled' ? (
                <div className="grid grid-cols-4 gap-2 text-center">
                  {FULFILLMENT_STEPS.map((step, idx) => {
                    const currentIdx = getStepIndex(lookedUpOrder.fulfillment_status);
                    const reached = idx <= currentIdx;
                    return (
                      <div
                        key={step.key}
                        className={`p-3 rounded-xl border ${
                          reached
                            ? 'bg-green-50 border-green-500 text-green-900 font-bold'
                            : 'bg-gray-50 border-gray-200 text-gray-400'
                        }`}
                      >
                        <div className="text-2xl mb-1">{step.icon}</div>
                        <div className="text-xs">{step.label}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 font-bold text-sm text-center">
                  This order was cancelled.
                </div>
              )}

              {/* Carrier & Tracking Number */}
              {(lookedUpOrder.tracking_number || lookedUpOrder.tracking_carrier) && (
                <div
                  className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  data-testid="tracked-carrier-box"
                >
                  <div>
                    <div className="text-xs font-bold uppercase text-yellow-800">Shipment Tracking</div>
                    <div className="text-sm font-bold text-gray-900 mt-0.5">
                      Carrier: {lookedUpOrder.tracking_carrier || 'Standard Parcel'} • Tracking #:{' '}
                      <span className="font-mono bg-white px-2 py-0.5 rounded border border-yellow-300">
                        {lookedUpOrder.tracking_number}
                      </span>
                    </div>
                  </div>
                  {lookedUpOrder.tracking_url && (
                    <a
                      href={lookedUpOrder.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-green-700 hover:bg-green-800 text-white text-xs font-bold py-2 px-4 rounded-lg text-center"
                    >
                      Track on Carrier Site ↗
                    </a>
                  )}
                </div>
              )}

              {/* Items & Totals */}
              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-xs font-bold uppercase text-gray-500 mb-3">Ordered Items</h3>
                <div className="divide-y divide-gray-100">
                  {(lookedUpOrder.items || []).map((item, i) => (
                    <div key={i} className="py-2.5 flex justify-between text-sm">
                      <div>
                        <span className="font-bold text-gray-900">{item.product_name}</span>
                        {item.variant_selection && (
                          <span className="text-xs text-gray-500 ml-1.5">({item.variant_selection})</span>
                        )}
                        <span className="text-xs text-gray-500 ml-2">× {item.quantity}</span>
                      </div>
                      <div className="font-semibold text-gray-900">
                        ${Number(item.line_total || 0).toFixed(2)} {lookedUpOrder.currency || 'CAD'}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between text-base font-extrabold text-green-900">
                  <span>
                    Total (incl. {lookedUpOrder.tax_name || 'HST'} {lookedUpOrder.tax_rate ?? 13}%)
                  </span>
                  <span>
                    ${Number(lookedUpOrder.total_amount || 0).toFixed(2)} {lookedUpOrder.currency || 'CAD'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Logged-in User Recent Orders List */}
        {myOrders.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
            <h2 className="text-lg font-extrabold text-green-900 mb-4">Your Recent Account Orders</h2>
            <div className="divide-y divide-gray-100">
              {myOrders.map((ord) => (
                <div
                  key={ord.order_number}
                  className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <div>
                    <span className="font-bold text-green-800">{ord.order_number}</span>
                    <span className="text-xs text-gray-500 ml-2">{ord.created_at}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 uppercase">
                      {ord.fulfillment_status}
                    </span>
                    <span className="font-bold">
                      ${Number(ord.total_amount).toFixed(2)} {ord.currency}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setOrderNumber(ord.order_number);
                        setEmail(ord.customer_email);
                        setLookedUpOrder(ord);
                      }}
                      className="text-xs font-bold text-green-700 hover:underline cursor-pointer"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTrackingPage;
